import React, { useEffect, useState } from "react";
import { Drawer, Modal, Input, Button, StatusBadge, LoadingBlock, toast } from "@/components/ds";
import { api, formatApiError, type Appointment, type AvailabilitySlot, type ReminderDoc } from "@/lib/api";
import { statusMeta, fmtDateTime, isoDate } from "./constants";
import { DispatchPanel } from "./DispatchSettings";
import { JobStatusPanel } from "./JobStatusPanel";

export function AppointmentDetails({ apptId, onClose, onChanged }: {
  apptId: string; onClose: () => void; onChanged: () => void;
}) {
  const [appt, setAppt] = useState<Appointment | null>(null);
  const [rem, setRem] = useState<ReminderDoc | null>(null);
  const [custLink, setCustLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  // reschedule state
  const [rDate, setRDate] = useState("");
  const [rSlots, setRSlots] = useState<AvailabilitySlot[] | null>(null);
  const [rSlot, setRSlot] = useState("");
  const [rLoading, setRLoading] = useState(false);

  const load = () => api.schedGetAppointment(apptId).then((a) => { setAppt(a); setRDate(isoDate(new Date(a.start_at))); }).catch((e) => toast.error(formatApiError(e)));
  const loadRem = () => api.remindersForAppointment(apptId).then((r) => { setRem(r.reminder); setCustLink(r.customer_link); }).catch(() => {});
  useEffect(() => { load(); loadRem(); /* eslint-disable-next-line */ }, [apptId]);

  const act = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const loadRSlots = async () => {
    if (!appt || !rDate) return;
    setRLoading(true); setRSlot(""); setRSlots(null);
    try {
      const r = await api.schedAvailability({ appointment_type_id: appt.appointment_type_id, date: rDate });
      setRSlots(r.slots);
    } catch (e) { toast.error(formatApiError(e)); } finally { setRLoading(false); }
  };
  useEffect(() => { if (showReschedule && rDate) loadRSlots(); /* eslint-disable-next-line */ }, [showReschedule, rDate]);

  const doReschedule = async () => {
    if (!rSlot) { toast.error("Pick a new time."); return; }
    await act(() => api.schedReschedule(apptId, rSlot), "Appointment rescheduled");
    setShowReschedule(false);
    onClose();
  };

  const m = appt ? statusMeta(appt.status) : null;
  const canModify = appt && !["cancelled", "completed", "no_show", "rescheduled"].includes(appt.status);

  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} title="Appointment" testid="appointment-details">
      {!appt ? <LoadingBlock rows={5} /> : (
        <div className="space-y-5">
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="appt-detail-summary">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-base font-semibold text-slate-900">{appt.contact_name ?? "—"}</p>
              <StatusBadge tone={m!.tone} data-testid="appt-detail-status">{m!.label}</StatusBadge>
            </div>
            <p className="text-sm text-slate-600">{appt.service_name ?? appt.requested_service ?? "Appointment"}</p>
            <p className="mt-1 text-sm text-slate-500">{fmtDateTime(appt.start_at)} — {new Date(appt.end_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</p>
            {appt.notes && <p className="mt-2 rounded-md bg-white p-2 text-sm text-slate-600">{appt.notes}</p>}
            {appt.cancel_reason && <p className="mt-2 text-xs text-red-600">Cancelled: {appt.cancel_reason}</p>}
          </div>

          {appt.intake_answers && Object.keys(appt.intake_answers).length > 0 && (
            <div data-testid="appt-detail-intake">
              <p className="mb-1.5 text-sm font-semibold text-slate-800">Intake answers</p>
              <div className="space-y-1">
                {Object.entries(appt.intake_answers).map(([k, v]) => (
                  <p key={k} className="text-sm text-slate-600"><span className="text-slate-400">{k}:</span> {String(v)}</p>
                ))}
              </div>
            </div>
          )}

          {/* Appointment reminders */}
          <div data-testid="appt-detail-reminders">
            <p className="mb-1.5 text-sm font-semibold text-slate-800">Reminders</p>
            {!rem ? <p className="text-xs text-slate-400">No reminders scheduled.</p> : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {rem.steps.map((s, i) => {
                    const tone = s.state === "sent" ? "green" : s.state === "unavailable" ? "amber" : s.state === "skipped" ? "slate" : "blue";
                    const label = s.state === "unavailable" ? "Unavailable — Config Required" : s.state === "sent" ? "Sent" : s.state === "skipped" ? "Skipped" : "Scheduled";
                    return (
                      <div key={i} className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs" data-testid={`appt-reminder-step-${i}`}>
                        <span className="capitalize text-slate-600">{s.kind === "confirmation" ? "Confirmation" : `${s.hours_before}h reminder`}</span>
                        <StatusBadge tone={tone as any}>{label}</StatusBadge>
                        {s.ai_generated && <StatusBadge tone="blue">AI</StatusBadge>}
                      </div>
                    );
                  })}
                </div>
                {custLink && (
                  <div className="flex items-center gap-2">
                    <Input value={custLink} readOnly className="text-xs" data-testid="appt-customer-link" />
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(custLink); toast.success("Customer link copied"); }} data-testid="appt-copy-link">Copy</Button>
                  </div>
                )}
                <p className="text-[11px] text-slate-400">Customers use this secure link to confirm, reschedule or cancel — no account needed.</p>
              </div>
            )}
          </div>

          {canModify && (
            <DispatchPanel apptId={apptId} assignedId={appt.assigned_technician_id} onChanged={() => { load(); onChanged(); }} />
          )}

          {canModify && (
            <JobStatusPanel appt={appt} onChanged={() => { load(); onChanged(); }} />
          )}

          {canModify && !showReschedule && (
            <div className="flex flex-wrap gap-2" data-testid="appt-detail-actions">
              {appt.status === "pending" && <Button size="sm" onClick={() => act(() => api.schedConfirm(apptId), "Confirmed")} disabled={busy} data-testid="appt-confirm">Confirm</Button>}
              <Button size="sm" variant="outline" onClick={() => setShowReschedule(true)} disabled={busy} data-testid="appt-reschedule-open">Reschedule</Button>
              <Button size="sm" variant="outline" onClick={() => act(() => api.schedComplete(apptId), "Marked completed")} disabled={busy} data-testid="appt-complete">Mark completed</Button>
              <Button size="sm" variant="outline" onClick={() => act(() => api.schedNoShow(apptId), "Marked no-show")} disabled={busy} data-testid="appt-no-show">No-show</Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmCancel(true)} disabled={busy} data-testid="appt-cancel-open" className="text-red-600">Cancel</Button>
            </div>
          )}

          {showReschedule && (
            <div className="space-y-3 rounded-lg border border-slate-200 p-3" data-testid="appt-reschedule-panel">
              <p className="text-sm font-semibold text-slate-800">Reschedule to a new open time</p>
              <Input type="date" value={rDate} onChange={(e) => setRDate(e.target.value)} data-testid="reschedule-date" />
              {rLoading ? <p className="text-sm text-slate-400">Loading times…</p>
                : rSlots === null ? null
                : rSlots.length === 0 ? <p className="text-sm text-slate-500" data-testid="reschedule-no-slots">No open times for this day.</p>
                : (
                  <div className="flex flex-wrap gap-2">
                    {rSlots.map((s) => (
                      <button key={s.start_at} onClick={() => setRSlot(s.start_at)} data-testid={`reschedule-slot-${s.local_time}`}
                        className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${rSlot === s.start_at ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"}`}>
                        {s.local_time}
                      </button>
                    ))}
                  </div>
                )}
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setShowReschedule(false)} disabled={busy}>Back</Button>
                <Button size="sm" onClick={doReschedule} disabled={busy || !rSlot} data-testid="reschedule-confirm">Confirm reschedule</Button>
              </div>
            </div>
          )}

          <ConfirmCancelModal open={confirmCancel} onOpenChange={setConfirmCancel} reason={cancelReason} setReason={setCancelReason} busy={busy}
            onConfirm={() => act(() => api.schedCancel(apptId, cancelReason || undefined), "Appointment cancelled").then(onClose)} />
        </div>
      )}
    </Drawer>
  );
}

function ConfirmCancelModal({ open, onOpenChange, reason, setReason, onConfirm, busy }: {
  open: boolean; onOpenChange: (o: boolean) => void; reason: string; setReason: (v: string) => void; onConfirm: () => void; busy: boolean;
}) {
  if (!open) return null;
  return (
    <Modal open onOpenChange={onOpenChange} title="Cancel appointment?" description="This frees the time slot for rebooking." testid="appt-cancel-confirm">
      <div className="space-y-4">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="cancel-reason" />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Keep it</Button>
          <Button className="bg-red-600 hover:bg-red-700" onClick={onConfirm} disabled={busy} data-testid="confirm-accept">Cancel appointment</Button>
        </div>
      </div>
    </Modal>
  );
}
